import { useState, useCallback } from 'react';
import { customerService, Customer } from '../services/customerService';
import { mapCustomerFormToInsert, type CustomerFormInput } from '../services/customerMapping';

export function useCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const getCustomerByReference = useCallback(async (reference: string) => {
    setLoading(true);
    setError(null);
    try {
      const customerData = await customerService.getCustomerByReference(reference);
      setCustomer(customerData);
      return customerData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCustomerByEmail = useCallback(async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const customerData = await customerService.getCustomerByEmail(email);
      setCustomer(customerData);
      return customerData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCustomerByPhone = useCallback(async (phone: string) => {
    setLoading(true);
    setError(null);
    try {
      const customerData = await customerService.getCustomerByPhone(phone);
      setCustomer(customerData);
      return customerData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchCustomers = useCallback(async (query: string) => {
    setLoading(true);
    setError(null);
    try {
      const customers = await customerService.searchCustomers(query);
      return customers;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search customers';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create local customer directly (no Edge Function)
  const createQuoteDockCustomer = useCallback(async (customerData: CustomerFormInput) => {
    setLoading(true);
    setError(null);
    try {
      const local = await customerService.createLocalCustomer(mapCustomerFormToInsert(customerData));
      return local as Customer;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create customer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLocalCustomer = useCallback(async (id: string, updates: any) => {
    setLoading(true);
    setError(null);
    try {
      const customer = await customerService.updateLocalCustomer(id, updates);
      setCustomer(customer);
      return customer;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCustomer = useCallback(() => {
    setCustomer(null);
    setError(null);
  }, []);

  return {
    customer,
    loading,
    error,
    getCustomerByReference,
    getCustomerByEmail,
    getCustomerByPhone,
    searchCustomers,
    createQuoteDockCustomer,
    updateLocalCustomer,
    clearCustomer,
  };
} 